import React, { createRef, useRef, useState } from 'react';
import { GetServerSideProps, NextPageWithLayout } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { Brand } from '@interfaces/models/brand';
import { startsWithLetter, accentFold } from '@helpers/utils/general';
import clsx from 'clsx';
import extractFromContext from '@helpers/utils/extract-from-context';
import Link from '@components/atoms/link/link';
import slugify from 'slugify';
import DefaultLayout from '@layouts/default-layout';
import { isRequest404Error, isRequestError } from '@helpers/errors';
import logger from '@helpers/utils/logger/client';
import { getBrands } from '@api/brands';
import { useTranslation } from 'next-i18next';
import { useBucket } from '@context/bucket.context';
import { FAST_FASHION_EDITO_URL } from '@helpers/utils/catalog/fast-fashion-ban-utils';
import Head from '@components/atoms/head/head';
import { usePreferences } from '@context/preferences.context';
import { defaultPageMetasForBrandPage } from '@constants/metas/default';
import CatalogHeader from '@components/catalog/catalog-header/catalog-header';
import { getAppInitialProps } from '@helpers/initial-props';
import styles from './index.module.scss';

export type BrandsPageProps = {
  brands: Brand[];
};

const Brands: NextPageWithLayout<BrandsPageProps> = (props) => {
  const { brands } = props;
  const { t } = useTranslation('brands');
  const { isFeatureEnabled } = useBucket();
  const { language } = usePreferences();
  const isFfBannerBrandsAzEnabled = isFeatureEnabled((f) => f.enableFfBannerBrandsAzWeb);

  // React ref to store array of refs
  const scrollRefs = useRef<React.RefObject<HTMLLIElement>[]>([]);

  // Names may include accented letters, which cause issues when sorting, so we normalize them here
  const [brandsWithNormalizedNames] = useState<Brand[]>(
    brands.map((brand: Brand) => {
      const path = `/${slugify(
        accentFold(brand.name.trim().toLowerCase())
          .replace('/', '-')
          .replace(/[^a-zA-Z0-9 -]/g, '')
          .trim(),
        {
          lower: true,
          trim: false,
          strict: true,
        },
      )}`;

      const pathWithSlash = path.endsWith('/') ? path : `${path}/`;

      return {
        ...brand,
        name: accentFold(brand.name.trim().toLowerCase()),
        url: {
          path: pathWithSlash,
        },
      };
    }),
  );

  // Create an array of all unique first letters and '#' symbol for ones starting with symbols and numbers
  const [firstLetters] = useState<string[]>([
    '#',
    ...Array.from(
      new Set(
        brandsWithNormalizedNames
          .map((brand: Brand) => brand.name.charAt(0))
          .filter((firstLetter: string) => startsWithLetter(firstLetter)),
      ),
    ).sort((a, b) => a.localeCompare(b)),
  ]);

  const [chunkedBrands] = useState<Brand[][]>([
    brandsWithNormalizedNames.filter((brand: Brand) => !startsWithLetter(brand.name)),
    ...firstLetters
      .slice(1)
      .map((firstLetter: string) =>
        brandsWithNormalizedNames.filter((brand: Brand) => brand.name.startsWith(firstLetter)),
      ),
  ]);

  // Populate scrollable refs, only create them once
  // if the selectedElements array length is expected to change there is a workaround
  scrollRefs.current = firstLetters.map((_, i) => scrollRefs.current[i] ?? createRef());

  // Curried handler to take index and return click handler
  const scrollSmoothHandler = (index: number) => () => {
    scrollRefs.current[index].current.scrollIntoView({
      block: 'start',
      behavior: 'smooth',
    });
  };

  // static breadcrumbs for brand page
  const breadCrumbsBrandsPage = [
    {
      localizedName: t('BRANDS.BREADCRUMBS_HOME'),
      url: {
        path: '/',
      },
    },
    {
      localizedName: t('BRANDS.BREADCRUMBS_DESIGNERS'),
      url: {
        path: '/brands/',
      },
    },
  ];

  return (
    <>
      <Head metadata={defaultPageMetasForBrandPage[language]} />
      {isFfBannerBrandsAzEnabled && (
        <div className={styles.brands__ffBanBanner}>
          {t('BRANDS.FF_BAN_BANNER.TITLE')}
          <Link
            href={FAST_FASHION_EDITO_URL}
            target={'__blank'}
          >
            {t('BRANDS.FF_BAN_BANNER.CTA')}
          </Link>
        </div>
      )}
      <main className={clsx('innerContainer', styles.brands)}>
        <CatalogHeader
          title={defaultPageMetasForBrandPage[language].headingTitle || ''}
          breadcrumbs={breadCrumbsBrandsPage}
          customClassName={styles.brands__header}
        />
        <div className={styles.brands__alphabeticNavigation}>
          {firstLetters.map((letter: string, index: number) => (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/interactive-supports-focus
            <a
              role="link"
              className={styles['brands__alphabeticNavigation--link']}
              onClick={scrollSmoothHandler(index)}
              key={letter}
            >
              {letter.toUpperCase()}
            </a>
          ))}
        </div>

        <ul>
          {chunkedBrands.map((brandListByFirstLetter: Brand[], index: number) => (
            <li
              // Since our list never re-renders, we don't need to have an actual key
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              className={styles.brand}
              ref={scrollRefs.current[index]}
            >
              <p className={clsx(styles.brand__firstLetter)}>
                {index === 0 ? '#' : brandListByFirstLetter[0].name.charAt(0)}
              </p>
              <ul className={clsx(styles.brand__list)}>
                {brandListByFirstLetter.map((brand: Brand) => (
                  <li
                    key={brand.id}
                    className={styles['brand__list--link']}
                  >
                    <Link
                      href={brand.url.path}
                      rel="internal-marques"
                    >
                      {brand.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </main>
    </>
  );
};

Brands.Layout = [DefaultLayout];
Brands.PageType = 'BrandsPage';

export const getServerSideProps: GetServerSideProps = async (context) => {
  context.res.setHeader('x-vc-page', 'staticPage');

  const { locale } = extractFromContext(context);

  try {
    const [initialProps, translations, brands] = await Promise.all([
      getAppInitialProps(context),
      serverSideTranslations(locale, ['default-layout', 'brands', 'common']),
      getBrands(),
    ]);

    return {
      props: {
        ...initialProps,
        ...translations,
        brands,
      },
    };
  } catch (error) {
    if (isRequestError(error)) {
      if (!isRequest404Error(error)) {
        logger.error(error, 'There was an api request error on the Brands Page - getServerSideProps - index.page.tsx');
      }
      return {
        notFound: true,
      };
    }
    logger.error(error, 'The server failed to render the Brands Page - getServerSideProps - index.page.tsx');
    throw error;
  }
};

export default Brands;
